import React, { useState } from 'react';
import { useOrganizationSettings, useUpdateOrganizationSettings, useExportOrganizationSettings, useImportOrganizationSettings } from '../../hooks/use-organization';
import { OrganizationSettingsImportModal } from './organization-settings-import-modal';
import { Card } from '../layout';
import { Button } from '../ui/button';
import { Input, Checkbox } from '../ui/form';
import { Download, Upload } from 'lucide-react';
import { 
  OrganizationSettings, 
  UpdateOrganizationSettingsRequest,
  SecuritySettings,
  AuthenticationSettings,
  IntegrationsSettings
} from '../../types';

interface OrganizationSettingsProps {
  className?: string;
}

export function OrganizationSettingsComponent({ className }: OrganizationSettingsProps) {
  const { data: settings, isLoading, isError, error } = useOrganizationSettings();
  const updateSettings = useUpdateOrganizationSettings();
  const exportSettings = useExportOrganizationSettings();
  
  const [showImportModal, setShowImportModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<OrganizationSettings | null>(null);

  // Initialize form data when settings are loaded
  React.useEffect(() => {
    if (settings && !formData) {
      setFormData(settings);
    }
  }, [settings, formData]);

  const handleEdit = () => {
    setEditMode(true);
    setFormData(settings || null);
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData(settings || null);
  };

  const handleSave = async () => {
    if (!formData) return;

    try {
      const request: UpdateOrganizationSettingsRequest = {
        security: formData.security,
        authentication: formData.authentication,
        integrations: formData.integrations,
      };

      await updateSettings.mutateAsync(request);
      setEditMode(false);
    } catch (error) {
      console.error('Failed to update organization settings:', error);
    }
  };

  const updateFormData = (
    section: keyof OrganizationSettings, 
    data: Partial<OrganizationSettings[keyof OrganizationSettings]>
  ) => {
    if (!formData) return;
    
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        ...data,
      },
    });
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <div className="flex items-center justify-center py-8">
          <div className="text-secondary-600 dark:text-secondary-400">Loading organization settings...</div>
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className={className}>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
              Error Loading Settings
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">
              {error?.message || 'Failed to load organization settings'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (!settings || !formData) {
    return (
      <Card className={className}>
        <div className="flex items-center justify-center py-8">
          <div className="text-secondary-600 dark:text-secondary-400">No organization settings found</div>
        </div>
      </Card>
    );
  }

  return (
    <div className={className}>
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
              Organization Settings
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Manage organization-wide configuration and settings
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
            >
              <Upload className="h-4 w-4 mr-1.5" />
              Import Settings
            </Button>
            <Button
              variant="outline"
              onClick={() => exportSettings.mutate()}
              disabled={exportSettings.isPending}
            >
              <Download className="h-4 w-4 mr-1.5" />
              {exportSettings.isPending ? 'Exporting…' : 'Export Settings'}
            </Button>
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={updateSettings.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateSettings.isPending}
                >
                  {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button onClick={handleEdit}>
                Edit Settings
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-8">
          {/* Integration Settings */}
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Integration Settings
            </h3>
            
            <div className="space-y-6">
              {/* Slack Webhook URL - HIDDEN */}
              {/* <Input
                label="Slack Webhook URL"
                value={formData?.integrations?.slackWebhook || ''}
                onChange={(e) => updateFormData('integrations', { slackWebhook: e.target.value })}
                disabled={!editMode}
                placeholder="https://hooks.slack.com/services/..."
                helperText="Optional: Webhook URL for sending notifications to Slack"
              /> */}

              <Input
                label="Email Provider"
                value={formData?.integrations?.emailProvider || ''}
                onChange={(e) => updateFormData('integrations', { emailProvider: e.target.value })}
                disabled={!editMode}
                placeholder="smtp"
                helperText="Email provider type (currently supports: smtp)"
              />

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  label="SMTP Server"
                  value={formData?.integrations?.smtpSettings?.server || ''}
                  onChange={(e) => updateFormData('integrations', { 
                    smtpSettings: { 
                      ...formData?.integrations?.smtpSettings, 
                      server: e.target.value 
                    } 
                  })}
                  disabled={!editMode}
                  placeholder="smtp.company.com"
                />

                <Input
                  label="Port"
                  type="number"
                  value={formData?.integrations?.smtpSettings?.port || 0}
                  onChange={(e) => updateFormData('integrations', { 
                    smtpSettings: { 
                      ...formData?.integrations?.smtpSettings, 
                      port: parseInt(e.target.value) || 0 
                    } 
                  })}
                  disabled={!editMode}
                  placeholder="587"
                  min={1}
                  max={65535}
                />

                <div className="flex items-center">
                  <Checkbox
                    label="Use TLS/SSL"
                    checked={formData?.integrations?.smtpSettings?.useTLS || false}
                    onChange={(e) => updateFormData('integrations', { 
                      smtpSettings: { 
                        ...formData?.integrations?.smtpSettings, 
                        useTLS: e.target.checked 
                      } 
                    })}
                    disabled={!editMode}
                    helperText="Enable encrypted connection to SMTP server"
                  />
                </div>

                <div className="flex items-center">
                  <Checkbox
                    label="Use StartTLS"
                    checked={formData?.integrations?.smtpSettings?.useStartTLS ?? true}
                    onChange={(e) => updateFormData('integrations', { 
                      smtpSettings: { 
                        ...formData?.integrations?.smtpSettings, 
                        useStartTLS: e.target.checked 
                      } 
                    })}
                    disabled={!editMode}
                    helperText="Use STARTTLS negotiation (recommended for port 587); port 465 always uses implicit SSL regardless of this setting"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Input
                  label="SMTP Username"
                  value={formData?.integrations?.smtpSettings?.username || ''}
                  onChange={(e) => updateFormData('integrations', { 
                    smtpSettings: { 
                      ...formData?.integrations?.smtpSettings, 
                      username: e.target.value 
                    } 
                  })}
                  disabled={!editMode}
                  placeholder="user@example.com"
                  helperText="Username for SMTP authentication"
                />

                <Input
                  label="SMTP Password"
                  type="password"
                  value={formData?.integrations?.smtpSettings?.password || ''}
                  onChange={(e) => updateFormData('integrations', { 
                    smtpSettings: { 
                      ...formData?.integrations?.smtpSettings, 
                      password: e.target.value 
                    } 
                  })}
                  disabled={!editMode}
                  placeholder="••••••••"
                  helperText="Password for SMTP authentication"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Input
                  label="From Email"
                  type="email"
                  value={formData?.integrations?.smtpSettings?.fromEmail || ''}
                  onChange={(e) => updateFormData('integrations', { 
                    smtpSettings: { 
                      ...formData?.integrations?.smtpSettings, 
                      fromEmail: e.target.value 
                    } 
                  })}
                  disabled={!editMode}
                  placeholder="noreply@company.com"
                  helperText="Sender email address for outgoing emails"
                />

                <Input
                  label="From Name"
                  value={formData?.integrations?.smtpSettings?.fromName || ''}
                  onChange={(e) => updateFormData('integrations', { 
                    smtpSettings: { 
                      ...formData?.integrations?.smtpSettings, 
                      fromName: e.target.value 
                    } 
                  })}
                  disabled={!editMode}
                  placeholder="Company Notifications"
                  helperText="Sender display name for outgoing emails"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {updateSettings.isError && (
        <Card className="mt-4 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
          <div className="text-red-800">
            <h4 className="font-semibold mb-2">Error Updating Settings</h4>
            <p className="text-sm">
              {updateSettings.error?.message || 'Failed to update organization settings'}
            </p>
          </div>
        </Card>
      )}

      <OrganizationSettingsImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
      />
    </div>
  );
}
